import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PlatformLayout from "../../components/platform/PlatformLayout";
import { PlatformZone } from "../../components/platform/PlatformZone";
import { PageRenderer } from "../../components/pageBuilder/PageRenderer";
import { FaArrowLeft } from "react-icons/fa";
import { platformApiService } from "../../services/platformApi";
import { IComponentNode } from "../../types/pageBuilder.types";
import { Spinner } from "components/ui";

const PolicyDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const [layout, setLayout] = useState<IComponentNode | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPolicy = async () => {
      try {
        // Ensure page exists with zones before fetching
        // This will auto-create the page if it doesn't exist with both top and bottom zones
        try {
          await platformApiService.ensurePlatformPageZones(slug!, [
            "top",
            "bottom",
          ]);
        } catch (ensureError) {
          // If ensure fails, continue anyway - page might already exist
          console.warn("Error ensuring zones:", ensureError);
        }

        // Fetch from platform pages API
        const response = await platformApiService.getPlatformPage(slug!);
        if (response.data?.page) {
          // setTitle(response.data.page.title);
          // Policy pages use layout AND zones
          if (response.data.page.layout) {
            setLayout(response.data.page.layout);
          } else {
            // Fallback: create a simple layout from content
            const content = response.data.page.content || "";
            setLayout({
              id: "policy-content",
              type: "container",
              props: {},
              styles: {
                default: {
                  maxWidth: "1024px",
                  margin: "0 auto",
                  padding: "48px 16px",
                },
              },
              children: [
                {
                  id: "policy-text",
                  type: "text",
                  props: { text: content },
                  styles: {
                    default: {
                      fontFamily:
                        "var(--store-font-family, 'Inter', sans-serif)",
                      fontSize: "16px",
                      lineHeight: "1.8",
                      color: "#374151",
                    },
                  },
                  children: [],
                },
              ],
            });
          }
        } else {
          // Fallback to default content
          // setTitle(t(`platform.policies.${slug}.title`) || slug || "Policy");
          const content =
            t(`platform.policies.${slug}.content`) || "Content not available.";
          setLayout({
            id: "policy-content",
            type: "container",
            props: {},
            styles: {
              default: {
                maxWidth: "1024px",
                margin: "0 auto",
                padding: "48px 16px",
              },
            },
            children: [
              {
                id: "policy-text",
                type: "text",
                props: { text: content },
                styles: {
                  default: {
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "16px",
                    lineHeight: "1.8",
                    color: "#374151",
                  },
                },
                children: [],
              },
            ],
          });
        }
      } catch (error) {
        // Fallback to default content
        // setTitle(t(`platform.policies.${slug}.title`) || slug || "Policy");
        const content =
          t(`platform.policies.${slug}.content`) || "Content not available.";
        setLayout({
          id: "policy-content",
          type: "container",
          props: {},
          styles: {
            default: {
              maxWidth: "1024px",
              margin: "0 auto",
              padding: "48px 16px",
            },
          },
          children: [
            {
              id: "policy-text",
              type: "text",
              props: { text: content },
              styles: {
                default: {
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "16px",
                  lineHeight: "1.8",
                  color: "#374151",
                },
              },
              children: [],
            },
          ],
        });
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchPolicy();
    }
  }, [slug, t]);

  if (loading) {
    return (
      <PlatformLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <Spinner />
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        </div>
      </PlatformLayout>
    );
  }

  return (
    <PlatformLayout>
      {/* Top Editable Zone - rendered from page record */}
      <PlatformZone pageSlug={slug || ""} zoneName="top" />

      <div className="bg-white pt-4 px-4">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/policies"
            className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6"
          >
            <FaArrowLeft className="mr-2" />
            {t("platform.policies.back") || "Back to Policies"}
          </Link>

            {/* <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8">
              {title}
              </h1> */}
            {layout?.children && layout.children.length && layout.children.length > 0 ? (
              <article className="bg-white pt-8 md:pt-12">
                <PageRenderer layout={layout} isPreview />
              </article>
            ) : null}
        </div>
      </div>

      {/* Bottom Editable Zone - rendered from page record */}
      <PlatformZone pageSlug={slug || ""} zoneName="bottom" />
    </PlatformLayout>
  );
};

export default PolicyDetail;
